import React, { useEffect, useRef } from 'react';
import portrait from '../assets/rumanSquare.webp';
import cv from '../assets/Md_Ruman_Islam_CV.pdf';
import './Home.css';

const HERO_TAGS = [
  'AI for 6G Networks',
  'Deep Reinforcement Learning',
  'Digital Twins',
  'UAV & Autonomous Systems',
];

const NETWORK_NODES = [
  { x: 0.08, y: 0.18, color: '#58d7ff' },
  { x: 0.22, y: 0.34, color: '#58d7ff' },
  { x: 0.16, y: 0.7, color: '#8ef3c7' },
  { x: 0.34, y: 0.16, color: '#f7c45f' },
  { x: 0.4, y: 0.46, color: '#8ef3c7' },
  { x: 0.34, y: 0.8, color: '#58d7ff' },
  { x: 0.56, y: 0.2, color: '#f7c45f' },
  { x: 0.6, y: 0.56, color: '#58d7ff' },
  { x: 0.52, y: 0.86, color: '#8ef3c7' },
  { x: 0.78, y: 0.3, color: '#f7c45f' },
  { x: 0.84, y: 0.64, color: '#58d7ff' },
];

const NETWORK_LINKS = [
  [0, 1],
  [1, 2],
  [0, 3],
  [1, 4],
  [2, 5],
  [3, 4],
  [4, 5],
  [3, 6],
  [4, 7],
  [5, 8],
  [6, 7],
  [7, 8],
  [6, 9],
  [7, 10],
  [9, 10],
];

const PULSE_COLORS = ['#58d7ff', '#8ef3c7', '#f7c45f'];

const HERO_FACTS = [
  { label: 'Current Role', value: 'Graduate Research Assistant' },
  { label: 'Institution', value: 'University of Nebraska Omaha' },
  { label: 'Past Industry', value: 'Samsung R&D Institute Bangladesh' },
];

export default function Home() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const pointer = { x: null, y: null, active: false };
    const pulses = NETWORK_LINKS.map((_, index) => ({
      progress: (index * 0.17) % 1,
      speed: 0.0024 + (index % 4) * 0.00045,
      radius: 2 + (index % 3),
      color: PULSE_COLORS[index % PULSE_COLORS.length],
    }));

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;

      const width = parent.clientWidth;
      const height = parent.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const drawGlow = (x, y, radius, color, alpha) => {
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
      gradient.addColorStop(0, `${color}${alpha}`);
      gradient.addColorStop(1, `${color}00`);
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawGrid = (width, height) => {
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
      ctx.lineWidth = 1;

      for (let x = 0; x < width; x += 44) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }

      for (let y = 0; y < height; y += 44) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }

      ctx.restore();
    };

    const onPointerMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      const touch = event.touches?.[0];
      pointer.x = (touch?.clientX ?? event.clientX) - rect.left;
      pointer.y = (touch?.clientY ?? event.clientY) - rect.top;
      pointer.active = true;
    };

    const onPointerLeave = () => {
      pointer.x = null;
      pointer.y = null;
      pointer.active = false;
    };

    const tick = () => {
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      ctx.clearRect(0, 0, width, height);

      drawGrid(width, height);

      const nodes = NETWORK_NODES.map((node) => ({
        ...node,
        px: node.x * width,
        py: node.y * height,
      }));

      ctx.save();
      ctx.lineWidth = 1.2;
      NETWORK_LINKS.forEach(([fromIndex, toIndex], linkIndex) => {
        const from = nodes[fromIndex];
        const to = nodes[toIndex];
        const hoverBoost =
          pointer.active && pointer.x !== null && pointer.y !== null
            ? Math.max(
                0,
                1 -
                  Math.hypot(pointer.x - (from.px + to.px) / 2, pointer.y - (from.py + to.py) / 2) /
                    180
              )
            : 0;

        ctx.strokeStyle = `rgba(116, 198, 255, ${0.12 + hoverBoost * 0.18})`;
        ctx.beginPath();
        ctx.moveTo(from.px, from.py);
        ctx.lineTo(to.px, to.py);
        ctx.stroke();

        const pulse = pulses[linkIndex];
        pulse.progress += pulse.speed;
        if (pulse.progress > 1) {
          pulse.progress -= 1;
        }

        const pulseX = from.px + (to.px - from.px) * pulse.progress;
        const pulseY = from.py + (to.py - from.py) * pulse.progress;

        drawGlow(pulseX, pulseY, 16, pulse.color, '55');
        ctx.fillStyle = pulse.color;
        ctx.beginPath();
        ctx.arc(pulseX, pulseY, pulse.radius, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();

      nodes.forEach((node, index) => {
        const activity = 0.55 + 0.45 * Math.sin(performance.now() * 0.0014 + index);
        drawGlow(node.px, node.py, 28 + activity * 16, node.color, '24');

        ctx.fillStyle = node.color;
        ctx.beginPath();
        ctx.arc(node.px, node.py, 4 + activity * 1.6, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(node.px, node.py, 8 + activity * 2.5, 0, Math.PI * 2);
        ctx.stroke();
      });

      if (pointer.active && pointer.x !== null && pointer.y !== null) {
        drawGlow(pointer.x, pointer.y, 84, '#58d7ff', '18');
      }

      rafRef.current = window.requestAnimationFrame(tick);
    };

    resize();
    tick();

    window.addEventListener('resize', resize);
    canvas.addEventListener('mousemove', onPointerMove);
    canvas.addEventListener('touchmove', onPointerMove, { passive: true });
    canvas.addEventListener('mouseleave', onPointerLeave);
    canvas.addEventListener('touchend', onPointerLeave);
    canvas.addEventListener('touchcancel', onPointerLeave);

    return () => {
      window.cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onPointerMove);
      canvas.removeEventListener('touchmove', onPointerMove);
      canvas.removeEventListener('mouseleave', onPointerLeave);
      canvas.removeEventListener('touchend', onPointerLeave);
      canvas.removeEventListener('touchcancel', onPointerLeave);
    };
  }, []);

  return (
    <section id="home" className="site-section hero-section" aria-labelledby="home-heading">
      <canvas ref={canvasRef} className="home-canvas" aria-hidden="true" />

      <div className="hero-shell">
        <div className="hero-copy">
          <p className="section-kicker">AI Systems, Networking, and Autonomy</p>
          <h1 id="home-heading">Md Ruman Islam</h1>
          <p className="hero-lede">
            Ph.D. Candidate in Computing &amp; Information Science at the
            University of Nebraska Omaha.
          </p>
          <p className="hero-summary">
            I build learning-driven communication and cyber-physical systems
            spanning AI for 6G networks, adaptive real-time data transport,
            intelligent digital twins, and UAV-based autonomy.
          </p>

          <div className="tag-list hero-tags" aria-label="Research focus areas">
            {HERO_TAGS.map((tag) => (
              <span key={tag} className="tag-pill">
                {tag}
              </span>
            ))}
          </div>

          <div className="hero-actions">
            <a href="#portfolio" className="action-button primary">View Publications</a>
            <a href={cv} target="_blank" rel="noopener noreferrer" className="action-button">Download CV</a>
            <a
              href="https://scholar.google.com/citations?user=cJZu7JgAAAAJ&hl=en&authuser=1"
              target="_blank"
              rel="noopener noreferrer"
              className="action-button"
            >
              Google Scholar
            </a>
          </div>
        </div>

        <aside className="hero-profile card-surface" aria-label="Current profile snapshot">
          <div className="hero-profile-header">
            <img
              src={portrait}
              alt="Portrait of Md Ruman Islam"
              className="hero-portrait"
            />
            <div>
              <p className="hero-role">Graduate Research Assistant</p>
              <p className="hero-org">University of Nebraska Omaha</p>
            </div>
          </div>

          <div className="hero-facts">
            {HERO_FACTS.map((fact) => (
              <div key={fact.label} className="hero-fact">
                <span>{fact.label}</span>
                <strong>{fact.value}</strong>
              </div>
            ))}
          </div>

          <div className="hero-note">
            Current work centers on AI for 6G communication networks, deep
            reinforcement learning for adaptive control, and field-ready
            autonomous systems research.
          </div>
        </aside>
      </div>
    </section>
  );
}
